import React, { useRef, useEffect } from 'react';

interface ShaderBGProps {
  intensity?: number;
  style?: React.CSSProperties;
}

export const ShaderBG: React.FC<ShaderBGProps> = ({ intensity = 1.3, style }) => {
  const ref = useRef<HTMLCanvasElement>(null);
  const prefersReduced = useRef(
    typeof window !== 'undefined'
      ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
      : false
  );

  useEffect(() => {
    if (prefersReduced.current) return; // no shader on reduced motion
    const canvas = ref.current;
    if (!canvas) return;
    const gl = canvas.getContext('webgl', { premultipliedAlpha: false, alpha: true });
    if (!gl) return;

    const vsrc = `
      attribute vec2 a_pos;
      void main() { gl_Position = vec4(a_pos, 0.0, 1.0); }
    `;
    
    const fsrc = `
      precision highp float;
      uniform vec2 u_res;
      uniform float u_time;
      uniform float u_intensity;

      vec3 mod289(vec3 x){return x-floor(x*(1.0/289.0))*289.0;}
      vec2 mod289(vec2 x){return x-floor(x*(1.0/289.0))*289.0;}
      vec3 permute(vec3 x){return mod289(((x*34.0)+1.0)*x);}
      float snoise(vec2 v){
        const vec4 C = vec4(0.211324865405187,0.366025403784439,-0.577350269189626,0.024390243902439);
        vec2 i  = floor(v + dot(v, C.yy) );
        vec2 x0 = v -   i + dot(i, C.xx);
        vec2 i1 = (x0.x>x0.y) ? vec2(1.0,0.0) : vec2(0.0,1.0);
        vec4 x12 = x0.xyxy + C.xxzz;
        x12.xy -= i1;
        i = mod289(i);
        vec3 p = permute( permute( i.y + vec3(0.0, i1.y, 1.0 ))+ i.x + vec3(0.0, i1.x, 1.0 ));
        vec3 m = max(0.5 - vec3(dot(x0,x0), dot(x12.xy,x12.xy), dot(x12.zw,x12.zw)), 0.0);
        m = m*m; m = m*m;
        vec3 x = 2.0 * fract(p * C.www) - 1.0;
        vec3 h = abs(x) - 0.5;
        vec3 ox = floor(x + 0.5);
        vec3 a0 = x - ox;
        m *= 1.79284291400159 - 0.85373472095314 * ( a0*a0 + h*h );
        vec3 g;
        g.x  = a0.x  * x0.x  + h.x  * x0.y;
        g.yz = a0.yz * x12.xz + h.yz * x12.yw;
        return 130.0 * dot(m, g);
      }

      float hash(vec2 p) {
        p = fract(p * vec2(123.34, 456.21));
        p += dot(p, p + 45.32);
        return fract(p.x * p.y);
      }

      float stars(vec2 uv, float t) {
        vec2 gridId = floor(uv * 32.0);
        vec2 gridUv = fract(uv * 32.0) - 0.5;
        
        float rand1 = hash(gridId);
        float rand2 = hash(gridId + 7.23);
        
        // Branchless star presence mask (only ~30% of cells contain stars)
        float starMask = step(0.70, rand1);
        
        vec2 starPos = vec2(rand1 - 0.5, rand2 - 0.5) * 0.7;
        float dist = length(gridUv - starPos);
        
        float twinkleSpeed = 1.0 + rand1 * 4.0;
        float twinkle = 0.25 + 0.75 * sin(t * twinkleSpeed + rand2 * 6.28);
        
        float size = 0.005 + rand2 * 0.010;
        float starGlow = smoothstep(size, 0.0, dist);
        
        return starGlow * twinkle * starMask;
      }

      void main() {
        vec2 uv = (gl_FragCoord.xy - 0.5*u_res.xy) / min(u_res.x, u_res.y);
        float t = u_time * 0.06; // slower temporal distortion for universe background

        // Space gas/dust simplex noise
        float n1 = snoise(uv*0.8 + vec2(t*0.4, -t*0.2));
        float n2 = snoise(uv*1.6 - vec2(-t*0.3, t*0.5));
        float n3 = snoise(uv*0.4 + vec2(t*0.1, t*0.1));
        float field = n1*0.50 + n2*0.32 + n3*0.18;

        float d = length(uv);
        float vignette = smoothstep(1.3, 0.25, d);

        // Palette: pure scale of blacks and extremely dark carbon/grey nebulae
        vec3 spaceVoid  = vec3(0.001, 0.001, 0.002);
        vec3 darkNebula = vec3(0.006, 0.006, 0.009);
        vec3 carbonDust = vec3(0.013, 0.013, 0.018);
        vec3 spaceGlow   = vec3(0.022, 0.022, 0.028);

        float plume = smoothstep(0.05, 0.95, field*0.5 + 0.5);
        vec3 col = spaceVoid;
        col = mix(col, darkNebula, plume * 0.75);
        col = mix(col, carbonDust, smoothstep(0.35, 0.82, plume) * 0.60);
        col = mix(col, spaceGlow, smoothstep(0.65, 0.96, plume) * 0.35 * u_intensity);

        // Multiple depth layers of twinkling stars
        float s1 = stars(uv, u_time);
        float s2 = stars(uv * 1.7 + vec2(15.23, 83.17), u_time * 0.8);
        float s3 = stars(uv * 2.4 - vec2(42.91, 19.34), u_time * 0.65) * 0.65;
        
        float starField = s1 + s2 + s3;
        col += vec3(starField) * vignette * 0.9;

        // Subtle slow ambient breathing glow
        col += spaceVoid * 2.0 * vignette * (0.5 + 0.5*sin(u_time*0.5));

        // grain
        float grain = fract(sin(dot(gl_FragCoord.xy, vec2(12.9898, 78.233))) * 43758.5453);
        col += (grain - 0.5) * 0.016;

        gl_FragColor = vec4(col, 1.0);
      }
    `;

    function compile(type: number, src: string) {
      const s = gl!.createShader(type);
      if (!s) return null;
      gl!.shaderSource(s, src);
      gl!.compileShader(s);
      if (!gl!.getShaderParameter(s, gl!.COMPILE_STATUS)) {
        console.error(gl!.getShaderInfoLog(s));
      }
      return s;
    }

    const vs = compile(gl.VERTEX_SHADER, vsrc);
    const fs = compile(gl.FRAGMENT_SHADER, fsrc);
    if (!vs || !fs) return;

    const prog = gl.createProgram();
    if (!prog) return;
    gl.attachShader(prog, vs);
    gl.attachShader(prog, fs);
    gl.linkProgram(prog);
    gl.useProgram(prog);

    const quad = new Float32Array([-1,-1, 1,-1, -1,1, -1,1, 1,-1, 1,1]);
    const buf = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(gl.ARRAY_BUFFER, quad, gl.STATIC_DRAW);
    const aPos = gl.getAttribLocation(prog, 'a_pos');
    gl.enableVertexAttribArray(aPos);
    gl.vertexAttribPointer(aPos, 2, gl.FLOAT, false, 0, 0);
    const uRes = gl.getUniformLocation(prog, 'u_res');
    const uTime = gl.getUniformLocation(prog, 'u_time');
    const uInt = gl.getUniformLocation(prog, 'u_intensity');

    function resize() {
      if (!canvas) return;
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = canvas.clientWidth * dpr;
      canvas.height = canvas.clientHeight * dpr;
      gl!.viewport(0, 0, canvas.width, canvas.height);
    }
    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(canvas);

    let raf: number;
    const t0 = performance.now();
    function tick() {
      if (!canvas) return;
      const t = (performance.now() - t0) / 1000;
      gl!.uniform2f(uRes, canvas.width, canvas.height);
      gl!.uniform1f(uTime, t);
      gl!.uniform1f(uInt, intensity);
      gl!.drawArrays(gl!.TRIANGLES, 0, 6);
      raf = requestAnimationFrame(tick);
    }
    tick();

    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      if (gl) {
        gl.deleteBuffer(buf);
        gl.deleteProgram(prog);
        gl.deleteShader(vs);
        gl.deleteShader(fs);
      }
    };
  }, [intensity]);

  if (prefersReduced.current) {
    return <div className="no-shader-bg" style={style} />;
  }

  return <canvas ref={ref} className="absolute inset-0 w-full h-full block pointer-events-none z-0" style={style} />;
};

export default ShaderBG;
